import {
  DOCUMENT,
  Inject,
  inject,
  Injectable,
  Optional,
  PLATFORM_ID,
  REQUEST,
  REQUEST_CONTEXT,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { environment } from '@src/environments/environment';
import { from, switchMap, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class B2bDeviceUtilService {
  devicePlugin: any;
  deviceInfo: any;
  deviceId: { identifier: string };

  modalHistory: string[] = [];
  isProgrammaticNavigation: boolean;

  request;
  requestContext:
    | {
        device_type?: 'desktop' | 'mobile';
      }
    | any;

  constructor(
    @Optional() @Inject('NAVIGATOR') private navigator: any,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    @Optional() @Inject('WINDOW') private window: any,
  ) {
    this.request = inject(REQUEST, { optional: true });
    this.requestContext = inject(REQUEST_CONTEXT, { optional: true });
    if (environment?.mobileApp) {
      from(this.initDeviceInfo()).subscribe();
    }
  }

  getMobileAppLink(): string {
    return environment?.mobileAppLink;
  }

  getMobileAppLinkBarCode(height?: number): string {
    if (!height) {
      height = 140;
    }
    return `${environment.imgHost}/new-assets/india/new-app/bar-codes/bag2bag-app-new-qr-code.png?height=${height}`;
  }

  isServer(): boolean {
    if (isPlatformServer(this.platformId)) {
      return true;
    } else {
      return false;
    }
  }

  isDesktop() {
    if (environment?.mobileApp) {
      return false;
    }
    if (isPlatformServer(this.platformId)) {
      const userAgent = this.request?.headers?.['user-agent']
        ? this.request?.headers?.['user-agent']
        : this.requestContext?.['user-agent'];
      return this.isDesktopScreenBySSR(
        userAgent,
        this.requestContext?.device_type,
      );
    } else {
      return this.isDesktopScreenByCSR(
        this.document?.defaultView?.navigator?.userAgent,
        this.document?.defaultView?.innerWidth,
      );
    }
  }

  getSSRHeader(): {
    requestHeaders: Headers | any;
    cind: string;
    rc: boolean;
  } {
    if (isPlatformServer(this.platformId)) {
      const body: {
        requestHeaders: Headers | any;
        cind: string;
        rc: boolean;
      } = {
        requestHeaders: null,
        cind: null,
        rc: null,
      };

      body.requestHeaders = this.request?.headers
        ? this.request?.headers
        : this.requestContext;

      if (this.request?.headers && this.request?.headers?.get('x-cind')) {
        body.cind = this.request?.headers?.get('x-cind');
      } else {
        body.cind = this.requestContext?.['x-cind'];
      }

      if (this.request?.headers && this.request?.headers?.get('x-rc')) {
        body.rc = this.request?.headers?.get('x-rc') === '1' ? true : false;
      } else {
        body.rc = this.requestContext?.['x-rc'] === '1' ? true : false;
      }

      return body;
      /*return this.request?.headers
        ? this.request?.headers
        : this.requestContext;*/
    } else {
      return null;
    }
  }

  isDesktopScreenBySSR(userAgent: string, deviceType: string) {
    const regexp = /android|iphone|kindle|ipad/i;
    if (regexp.test(userAgent)) {
      return false;
    } else {
      return true;
    }
  }

  isDesktopScreenByCSR(userAgent: string, width: number) {
    if (environment?.webApp) {
      if (width < 992) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  getWidth(): number {
    return this.document?.defaultView?.innerWidth;
  }

  getHeight(): number {
    return this.document?.defaultView?.innerHeight;
  }

  getWebVendor() {
    return this.document?.defaultView?.navigator?.vendor;
  }

  getWebOS() {
    return this.getOS(this.navigator?.appVersion);
  }

  getOS(appVersion: string): string {
    if (appVersion?.indexOf('Win') !== -1) {
      return 'Windows';
    }

    if (appVersion?.indexOf('Mac') !== -1) {
      return 'MacOS';
    }

    if (appVersion?.indexOf('Linux') !== -1) {
      return 'Linux';
    }

    return 'Unknown OS';
  }

  getWebPlatform(): string {
    return this.getPlatformByUserAgent(this.navigator?.userAgent);
  }

  getAppPlatform() {
    return this.getPlatformByUserAgent(
      this.document?.defaultView?.navigator?.userAgent,
    );
  }

  getPlatformByUserAgent(userAgent: string) {
    if (/android|sink/i.test(userAgent)) {
      return 'android';
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'ios';
    } else if (/Win/.test(userAgent)) {
      return 'web';
    } else if (/Mac/i.test(userAgent)) {
      return 'web';
    } else {
      return 'web';
    }
  }

  async initDeviceInfo() {
    if (!this.devicePlugin) {
      const module = await import('@capacitor/device');
      this.devicePlugin = module?.Device;
    }
    if (!this.deviceInfo) {
      this.deviceInfo = await this.devicePlugin?.getInfo();
    }
    if (!this.deviceId) {
      this.deviceId = await this.devicePlugin?.getId();
    }
  }

  getDeviceInfo() {
    if (this.devicePlugin) {
      return from(this.devicePlugin?.getInfo()).pipe(
        tap((info) => {
          this.deviceInfo = info;
        }),
      );
    } else {
      return from(import('@capacitor/device')).pipe(
        switchMap((module) => {
          this.devicePlugin = module?.Device;
          return module?.Device?.getInfo();
        }),
        tap((info) => {
          this.deviceInfo = info;
        }),
      );
    }
  }

  async openSite(url: string, windowName?: string) {
    if (!windowName) {
      windowName = '_blank';
    }
    try {
      import('@capacitor/browser').then((module) => {
        module?.Browser?.open({ url, windowName });
      });
    } catch (e) {
      console.error('Error while opening the url in the browser');
    }
  }

  pushFakeModalStateInHistory(key: string) {
    if (!this.isServer() && !this?.isDesktop() && environment?.webApp) {
      const modalState = {
        key,
        modal: true,
      };
      if (!this.modalHistory?.includes(key)) {
        this.modalHistory?.push(key);
      }

      this.window?.history?.pushState(modalState, null);
    }
  }

  removeFakeModalStateFromHistory(key: string) {
    if (!this.isServer() && !this?.isDesktop() && environment?.webApp) {
      if (
        this.window?.history?.state?.modal &&
        this.window?.history?.state?.key === key
      ) {
        const lastIndex: number = this.modalHistory?.lastIndexOf(key);
        if (lastIndex !== -1) {
          this.modalHistory?.splice(lastIndex, 1);
        }
        this.isProgrammaticNavigation = true;
        this.window?.history?.back();
      } else {
        if (key && this.window?.history?.state?.modal) {
          if (this.modalHistory?.includes(key)) {
            const lastIndex: number = this.modalHistory?.lastIndexOf(key);
            if (lastIndex !== -1) {
              this.modalHistory?.splice(lastIndex, 1);
            }
            this.isProgrammaticNavigation = true;
            this.window?.history?.back();
          }
        }
      }
    }
  }

  popModalHistory() {
    if (this.modalHistory?.length > 0) {
      this.modalHistory?.pop();
    }
  }

  resetModalHistory() {
    this.modalHistory = [];
  }

  getCurrentPageUrl(): string {
    return this.document?.defaultView?.location?.href;
  }

  getCurrentPath(): string {
    return this.document?.defaultView?.location?.pathname;
  }

  isWebglSupported() {
    if (this.document?.defaultView?.WebGLRenderingContext) {
      const canvas = this.document.createElement('canvas');
      try {
        // Note that { failIfMajorPerformanceCaveat: true } can be passed as a second argument
        // to canvas.getContext(), causing the check to fail if hardware rendering is not available. See
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/getContext
        // for more details.
        const context =
          canvas?.getContext('webgl2') || canvas?.getContext('webgl');
        if (context && typeof context?.getParameter == 'function') {
          return true;
        } else {
          console.warn('WebGL is supported, but disabled ');
        }
      } catch (e) {
        // WebGL is supported, but disabled
        console.warn('WebGL is supported, but disabled ', e);
      }
      return false;
    }
    console.warn('WebGL is not supported');
    // WebGL not supported
    return false;
  }
}
